import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bookmark,
  BookOpen,
  Guitar,
  Music,
  Activity,
  Trash2,
  Target,
  Zap,
  ChevronRight,
  ClipboardList,
  Users,
} from "lucide-react";
import { useTaskPlayer } from "../../hooks/useTaskPlayer";
import { useStudentAuth } from "../../hooks/useStudentAuth";
import { useStudentClassrooms } from "../../hooks/useStudentClassrooms";
import { useStudentTasks } from "../../hooks/useStudentTasks";
import { useStudentFavorites } from "../../hooks/useStudentFavorites";
import { useStudentStats } from "../../hooks/useStudentStats";
import { JoinClassroomPanel } from "./JoinClassroomPanel";
import {
  TASK_TYPE_LABELS,
  TASK_TYPE_ICONS,
  TASK_TYPE_COLORS,
  type SongTaskConfig,
  type Task,
} from "../../lib/taskEngine";
import { saveTaskSongSession, TASK_SONG_ROUTE_ID } from "../../lib/taskSongBridge";
import { normalizeClassroomCode } from "../../lib/classroomEngine";

import { HOME_THEME as HT } from "../../lib/homeTheme";

const C = {
  border: "rgba(232, 224, 212, 0.1)",
  muted: "#a09890",
  dim: "#6a635c",
  text: "#f5f0eb",
  accent: "#c9405a",
  bgCard: "rgba(36, 34, 32, 0.85)",
  teal: "#14b8a6",
};

interface StudentTasksHubProps {
  className?: string;
  variant?: "card" | "index";
  /** Si se indica, solo muestra tareas de esa clase y valida códigos manuales. */
  classroomCode?: string;
}

export const StudentTasksHub: React.FC<StudentTasksHubProps> = ({
  className = "",
  variant = "card",
  classroomCode,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, loadTask, reset } = useTaskPlayer();
  const { profile, user } = useStudentAuth();
  const { favorites, removeFavorite } = useStudentFavorites();
  const { stats, getRecentTasks, recordExploration } = useStudentStats();
  const [codeInput, setCodeInput] = useState("");
  const [scopeError, setScopeError] = useState<string | null>(null);

  const studentId = profile?.uid ?? user?.uid;
  const studentName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : user?.displayName?.trim() || "Estudiante";

  const { memberships } = useStudentClassrooms(studentId, studentName);
  const { tasks: assignedTasks, loading: assignedLoading } = useStudentTasks(
    studentId,
    memberships
  );

  const scopedClassroom = classroomCode ? normalizeClassroomCode(classroomCode) : null;

  const scopedTasks = useMemo(() => {
    if (!scopedClassroom) return assignedTasks;
    return assignedTasks.filter(
      (t) => t.classroomId && normalizeClassroomCode(t.classroomId) === scopedClassroom
    );
  }, [assignedTasks, scopedClassroom]);

  const taskBelongsToScope = useCallback(
    (task: Task | null) => {
      if (!task || !scopedClassroom) return true;
      return (
        Boolean(task.classroomId) &&
        normalizeClassroomCode(task.classroomId!) === scopedClassroom
      );
    },
    [scopedClassroom]
  );

  useEffect(() => {
    const fromUrl = searchParams.get("task")?.trim().toUpperCase();
    if (fromUrl && fromUrl.length >= 4 && !state.task) {
      setScopeError(null);
      void loadTask(fromUrl, studentId);
      setCodeInput(fromUrl);
    }
  }, [searchParams, loadTask, state.task, studentId]);

  useEffect(() => {
    if (!state.task || !scopedClassroom) return;
    if (!taskBelongsToScope(state.task)) {
      reset();
      setCodeInput("");
      setSearchParams({}, { replace: true });
      setScopeError("Esta tarea pertenece a otra clase.");
    }
  }, [state.task, scopedClassroom, taskBelongsToScope, reset, setSearchParams]);

  const taskBlocked = Boolean(state.task && (state.error || scopeError));

  const handleStartTask = (task: Task) => {
    if (taskBlocked) return;

    if (task.type === "song") {
      const cfg = task.config as SongTaskConfig;
      if (!cfg.musicXml?.trim()) {
        return;
      }
      saveTaskSongSession({
        taskId: task.id,
        teacherId: task.teacherId,
        title: cfg.songTitle || task.title,
        musicXml: cfg.musicXml,
        youtubeUrl: cfg.youtubeUrl,
        tempo: cfg.tempo,
        classroomId: task.classroomId,
      });
      navigate(`/songs/${TASK_SONG_ROUTE_ID}`, {
        state: { task, studentProfile: profile },
      });
      return;
    }

    navigate("/practice", { state: { task, studentProfile: profile } });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    const code = codeInput.trim().toUpperCase();
    setScopeError(null);
    setSearchParams({ task: code }, { replace: true });
    await loadTask(code, studentId);
  };

  const clearLoadedTask = () => {
    reset();
    setCodeInput("");
    setScopeError(null);
    setSearchParams({}, { replace: true });
  };

  const openAssignedTask = async (task: Task) => {
    setCodeInput(task.id);
    setScopeError(null);
    setSearchParams({ task: task.id }, { replace: true });
    await loadTask(task.id, studentId);
  };

  const startLabel = useMemo(() => {
    if (!state.task) return "Empezar tarea";
    if (state.task.type === "song") return "Abrir pieza en estudio";
    return "Empezar tarea";
  }, [state.task]);

  const isIndex = variant === "index";

  const displayError = scopeError || state.error;

  const assignedList = (
    <>
      {studentId && !state.task && scopedTasks.length > 0 && (
        <ul>
          {scopedTasks.map((task, i) => (
            <li key={task.id} style={{ borderBottom: `1px solid ${HT.lineItem}` }}>
              <button
                type="button"
                onClick={() => void openAssignedTask(task)}
                className="group flex w-full items-baseline gap-4 md:gap-8 py-5 md:py-6 text-left transition-all duration-300 hover:pl-2 hover:bg-white/[0.02]"
              >
                <span className="w-8 shrink-0 text-xs tabular-nums" style={{ color: HT.dim }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="flex-1 font-light text-[clamp(1.125rem,2.5vw,1.75rem)] tracking-tight group-hover:text-[#ddd6fe] transition-colors"
                  style={{ color: HT.text }}
                >
                  {task.title}
                </span>
                <span className="text-xs tracking-wide shrink-0" style={{ color: HT.muted }}>
                  {TASK_TYPE_LABELS[task.type]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {studentId && !state.task && assignedLoading && (
        <p className="py-4 text-xs tracking-widest uppercase" style={{ color: HT.dim }}>
          Cargando tareas…
        </p>
      )}
      {studentId && !state.task && !assignedLoading && scopedTasks.length === 0 && scopedClassroom && (
        <p className="py-4 text-sm" style={{ color: HT.muted }}>
          Tu profesor aún no ha asignado tareas a esta clase.
        </p>
      )}
    </>
  );

  const codeForm = !state.task ? (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-end">
      <div className="flex-1">
        <label className="block text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: HT.muted }}>
          Código de tarea (6 caracteres)
        </label>
        <input
          type="text"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
          maxLength={6}
          placeholder="X7K9P2"
          className="w-full bg-transparent border-0 border-b pb-2 font-mono text-xl font-light tracking-[0.3em] uppercase focus:outline-none focus:border-[#c4b5fd]"
          style={{ color: HT.text, borderColor: HT.lineItem }}
        />
      </div>
      <button
        type="submit"
        disabled={state.isLoading || codeInput.length < 4}
        className="text-[11px] tracking-[0.18em] uppercase pb-2 disabled:opacity-30 transition-colors hover:text-white"
        style={{ color: HT.muted }}
      >
        {state.isLoading ? "Buscando…" : "Buscar"}
      </button>
      {displayError && (
        <p className="w-full text-sm sm:col-span-2" style={{ color: "#fca5a5" }}>
          {displayError}
        </p>
      )}
    </form>
  ) : null;

  const loadedTask = state.task ? (
    <div className="py-6 space-y-4" style={{ borderTop: `1px solid ${HT.lineItem}` }}>
      <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: HT.muted }}>
        {TASK_TYPE_LABELS[state.task.type]}
      </p>
      <p className="font-light text-2xl md:text-3xl tracking-tight" style={{ color: HT.text }}>
        {state.task.title}
      </p>
      {displayError && (
        <p className="text-sm" style={{ color: "#fca5a5" }}>
          {displayError}
        </p>
      )}
      <div className="flex flex-wrap gap-6">
        <button
          type="button"
          onClick={() => handleStartTask(state.task!)}
          disabled={taskBlocked}
          className="text-[11px] tracking-[0.18em] uppercase disabled:opacity-30 hover:text-[#ddd6fe] transition-colors"
          style={{ color: HT.text }}
        >
          {startLabel}
        </button>
        <button
          type="button"
          onClick={clearLoadedTask}
          className="text-[11px] tracking-[0.18em] uppercase hover:text-white/80 transition-colors"
          style={{ color: HT.dim }}
        >
          Otro código
        </button>
      </div>
    </div>
  ) : null;

  if (isIndex) {
    return (
      <div className={className}>
        {assignedList}
        {codeForm}
        {loadedTask}
      </div>
    );
  }

  return (
    <section
      className={`rounded-[20px] p-5 backdrop-blur-md space-y-5 ${className}`}
      style={{ background: C.bgCard, border: `1px solid ${C.border}` }}
    >
      <div className="flex items-center gap-2">
        <ClipboardList className="h-5 w-5" style={{ color: C.accent }} />
        <h3 className="font-serif text-lg font-bold" style={{ color: C.text }}>
          Tareas del profesor
        </h3>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Target, label: "Tareas", value: stats.tasksCompleted, color: "#34d399" },
          { icon: Zap, label: "Racha", value: stats.currentStreak, color: "#fb923c" },
          {
            icon: Activity,
            label: "Minutos",
            value: Math.round(stats.totalPracticeMinutes),
            color: "#60a5fa",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl p-3 text-center"
            style={{ background: "rgba(0,0,0,0.25)", border: `1px solid ${C.border}` }}
          >
            <s.icon className="mx-auto mb-1 h-4 w-4" style={{ color: s.color }} />
            <p className="font-serif text-xl font-black" style={{ color: C.text }}>
              {s.value}
            </p>
            <p className="text-[9px] uppercase tracking-wider" style={{ color: C.dim }}>
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* Unirse a clase — solo fuera del detalle de una clase */}
      {studentId && !classroomCode && (
        <div
          className="rounded-xl p-4 space-y-2"
          style={{ background: "rgba(20,184,166,0.06)", border: `1px solid rgba(20,184,166,0.2)` }}
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" style={{ color: C.teal }} />
            <p className="text-sm font-semibold" style={{ color: C.text }}>
              Código de clase
            </p>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            Tu profesor te da un código de <strong>8 caracteres</strong> para unirte a la clase.
            Las tareas con MusicXML restringidas a la clase solo funcionan después de unirte.
          </p>
          <JoinClassroomPanel
            studentId={studentId}
            studentName={studentName}
            compact
            onJoined={() => {
              if (codeInput.trim()) {
                void loadTask(codeInput.trim(), studentId);
              }
            }}
          />
        </div>
      )}

      {!studentId && (
        <p className="text-xs rounded-xl px-3 py-2" style={{ background: "rgba(0,0,0,0.25)", color: C.muted }}>
          Inicia sesión con tu cuenta Edusico para unirte a una clase y entregar tareas.
        </p>
      )}

      {/* Tareas asignadas por el profesor (sin código manual) */}
      {studentId && !state.task && (
        <div
          className="rounded-xl p-4 space-y-3"
          style={{ background: "rgba(201,64,90,0.06)", border: `1px solid rgba(201,64,90,0.2)` }}
        >
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" style={{ color: C.accent }} />
            <p className="text-sm font-semibold" style={{ color: C.text }}>
              Tareas de tu clase
            </p>
          </div>
          {assignedLoading ? (
            <p className="text-xs" style={{ color: C.muted }}>
              Cargando tareas…
            </p>
          ) : scopedTasks.length === 0 ? (
            <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
              Cuando tu profesor asigne una tarea a tu clase, aparecerá aquí. También puedes usar el
              código de 6 caracteres abajo.
            </p>
          ) : (
            <ul className="space-y-2">
              {scopedTasks.map((task) => (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() => void openAssignedTask(task)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-white/5"
                    style={{
                      background: "rgba(0,0,0,0.25)",
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
                      style={{ backgroundColor: TASK_TYPE_COLORS[task.type] }}
                    >
                      {TASK_TYPE_ICONS[task.type]}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold" style={{ color: C.text }}>
                        {task.title}
                      </span>
                      <span className="block text-[10px]" style={{ color: C.dim }}>
                        {TASK_TYPE_LABELS[task.type]} · {task.teacherName || "Profesor"}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" style={{ color: C.muted }} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!state.task ? (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm" style={{ color: C.muted }}>
            Cuando el profesor te asigne una tarea, ingresa el código de <strong>6 caracteres</strong>{" "}
            (distinto al código de clase).
          </p>
          <input
            type="text"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            maxLength={6}
            placeholder="Ej: X7K9P2"
            className="w-full rounded-xl px-4 py-3 text-center font-mono text-xl font-bold tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-[#c9405a]/40"
            style={{
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${C.border}`,
              color: C.text,
            }}
          />
          <button
            type="submit"
            disabled={state.isLoading || codeInput.length < 4}
            className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: C.accent }}
          >
            {state.isLoading ? "Buscando…" : "Buscar tarea"}
          </button>
          {displayError && (
            <p className="text-center text-sm" style={{ color: "#fb7185" }}>
              {displayError}
            </p>
          )}
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl font-bold text-white"
              style={{ backgroundColor: TASK_TYPE_COLORS[state.task.type] }}
            >
              {TASK_TYPE_ICONS[state.task.type]}
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.dim }}>
                {TASK_TYPE_LABELS[state.task.type]}
              </p>
              <h4 className="truncate font-serif text-lg font-bold" style={{ color: C.text }}>
                {state.task.title}
              </h4>
            </div>
          </div>
          {state.task.description && (
            <p className="text-sm" style={{ color: C.muted }}>
              {state.task.description}
            </p>
          )}
          {state.task.type === "song" && (
            <p className="text-xs rounded-lg px-3 py-2" style={{ background: "rgba(201,64,90,0.1)", color: C.muted }}>
              Pieza con partitura MusicXML — se abrirá en el estudio de canciones (Aprender / Practicar).
            </p>
          )}
          <div
            className="rounded-xl p-3 text-xs space-y-1.5"
            style={{ background: "rgba(0,0,0,0.25)", color: C.muted }}
          >
            <div className="flex justify-between">
              <span>Profesor</span>
              <span className="font-medium" style={{ color: C.text }}>
                {state.task.teacherName || "Profesor"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Validación</span>
              <span className="font-medium" style={{ color: C.text }}>
                {state.task.validation.useAudio ? "Micrófono" : "Visual"}
              </span>
            </div>
            {state.task.classroomId && (
              <div className="flex justify-between">
                <span>Clase</span>
                <span className="font-medium font-mono" style={{ color: C.teal }}>
                  {state.task.classroomId}
                </span>
              </div>
            )}
          </div>
          {displayError && (
            <p className="text-sm rounded-xl px-3 py-2" style={{ background: "rgba(251,113,133,0.1)", color: "#fb7185" }}>
              {displayError}
            </p>
          )}
          <button
            type="button"
            onClick={() => handleStartTask(state.task!)}
            disabled={taskBlocked}
            className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: taskBlocked ? "rgba(255,255,255,0.15)" : "#5A7A3A" }}
          >
            {startLabel}
          </button>
          <button
            type="button"
            onClick={clearLoadedTask}
            className="w-full py-1 text-xs"
            style={{ color: C.dim }}
          >
            Ingresar otro código
          </button>
        </div>
      )}

      {favorites.length > 0 && !state.task && (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.dim }}>
            Exploraciones guardadas
          </p>
          <div className="space-y-1.5">
            {favorites.slice(0, 4).map((fav) => (
              <div key={fav.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(fav.url)}
                  className="flex flex-1 items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors hover:bg-white/[0.06]"
                  style={{ color: C.muted }}
                >
                  <Bookmark className="h-3.5 w-3.5 shrink-0" style={{ color: "#fbbf24" }} />
                  <span className="truncate">{fav.title}</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeFavorite(fav.id)}
                  className="rounded-md p-1.5 hover:bg-white/10"
                  style={{ color: C.dim }}
                  title="Eliminar"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {getRecentTasks().length > 0 && !state.task && (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.dim }}>
            Historial reciente
          </p>
          <div className="space-y-1.5">
            {getRecentTasks(3).map((task) => (
              <div
                key={task.taskId}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs"
                style={{ background: "rgba(0,0,0,0.2)", color: C.muted }}
              >
                <Zap className="h-3.5 w-3.5 shrink-0" style={{ color: "#fbbf24" }} />
                <span className="flex-1 truncate">{task.title}</span>
                <span className="font-bold" style={{ color: C.text }}>
                  {task.score}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!state.task && (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.dim }}>
            Explorar libremente
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Escalas", icon: BookOpen, color: "#FF4D4D", path: "/scales" },
              { label: "Acordes", icon: Guitar, color: "#F59E0B", path: "/chords" },
              { label: "Modos", icon: Music, color: "#06B6D4", path: "/modos" },
              { label: "Notas", icon: Activity, color: "#22C55E", path: "/notas" },
            ].map((item) => (
              <button
                key={item.path}
                type="button"
                onClick={() => {
                  recordExploration();
                  navigate(item.path);
                }}
                className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-xs transition-colors hover:bg-white/[0.06]"
                style={{ border: `1px solid ${C.border}`, color: C.muted }}
              >
                <item.icon className="h-4 w-4 shrink-0" style={{ color: item.color }} />
                <span>{item.label}</span>
                <ChevronRight className="ml-auto h-3.5 w-3.5" style={{ color: C.dim }} />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
